import React, { useState } from "react";
import { Heart, Copy, Check, ExternalLink, Building2, HandHeart, BadgeCheck } from "lucide-react";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { CONTACT } from "../data/mock";

const DONATION_TYPES = [
  {
    id: "tesseramento",
    label: "Tesseramento",
    title: "Bonifico per il Tesseramento",
    description:
      "Quota associativa annuale di € 50,00. Effettua il bonifico all'IBAN ufficiale indicando nella causale il tipo di tesseramento.",
    causale: "Tesseramento A.C.D.T. – Nome Cognome",
    icon: BadgeCheck,
  },
  {
    id: "volontaria",
    label: "Donazione Volontaria",
    title: "Donazione libera a sostegno dell'Associazione",
    description:
      "Sostieni le attività di A.C.D.T. con un contributo libero a tua scelta. Ogni donazione ci aiuta a promuovere formazione, tutela digitale e cultura tecnologica.",
    causale: "Donazione liberale A.C.D.T. – Nome Cognome",
    icon: HandHeart,
  },
];

export default function Donate() {
  const [activeType, setActiveType] = useState("tesseramento");
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const current = DONATION_TYPES.find((d) => d.id === activeType) || DONATION_TYPES[0];
  const ActiveIcon = current.icon;

  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT.donationIban);
      setCopied(true);
      toast({ title: "IBAN copiato", description: "Il codice IBAN è stato copiato negli appunti." });
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      toast({ title: "Impossibile copiare", description: "Seleziona e copia manualmente l'IBAN." });
    }
  };

  return (
    <section id="dona" className="relative py-24 md:py-28 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-60"></div>
      <div className="orb bg-[#f59e0b]" style={{ width: 420, height: 420, top: -120, right: -80, opacity: 0.25 }}></div>
      <div className="orb bg-[#102a6b]" style={{ width: 420, height: 420, bottom: -120, left: -80, opacity: 0.18 }}></div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto reveal">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 backdrop-blur border border-slate-200 text-[12px] font-semibold text-[#102a6b] shadow-sm">
            <Heart className="h-3.5 w-3.5 text-[#e11d48]" />
            Sostieni la nostra missione
          </div>
          <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-[#0a1f55] leading-[1.05]">
            Dona all'<span className="text-[#102a6b]">Associazione A.C.D.T.</span>
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">
            Scegli se versare la quota di <strong>tesseramento</strong> o una{" "}
            <strong>donazione volontaria</strong> libera per sostenere le attività dell'Associazione.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-6">
          {/* IBAN Donation Card */}
          <div className="lg:col-span-3 reveal relative rounded-3xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-8 md:p-10 overflow-hidden">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#f59e0b]/25 blur-3xl"></div>
            <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-3xl"></div>

            <div className="relative">
              {/* Type Toggle */}
              <div className="inline-flex p-1 rounded-xl bg-white/[0.08] border border-white/15 backdrop-blur-sm">
                {DONATION_TYPES.map((t) => {
                  const TIcon = t.icon;
                  const active = t.id === activeType;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setActiveType(t.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-bold transition-all ${
                        active
                          ? "bg-[#f59e0b] text-[#0a1f55] shadow-md"
                          : "text-slate-200 hover:text-white"
                      }`}
                    >
                      <TIcon className="h-4 w-4" />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-[#f59e0b] text-[#0a1f55] flex items-center justify-center">
                  <ActiveIcon className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#fbbf24]">
                    Bonifico bancario diretto
                  </div>
                  <h3 className="text-xl md:text-2xl font-extrabold mt-0.5">{current.title}</h3>
                </div>
              </div>

              <p className="mt-5 text-slate-200/90 leading-relaxed text-[15px]">{current.description}</p>

              <div className="mt-7 rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur-sm p-5 md:p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-white text-[#E2001A] flex items-center justify-center font-extrabold text-sm">
                      UC
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-300">Banca</div>
                      <div className="font-bold text-white">{CONTACT.donationBank}</div>
                    </div>
                  </div>
                  <Building2 className="h-5 w-5 text-[#fbbf24]" />
                </div>

                <div className="mt-5">
                  <div className="text-[11px] uppercase tracking-wider text-slate-300 font-semibold">IBAN</div>
                  <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-[#06143d] border border-white/10 px-4 py-3">
                    <code className="flex-1 text-[15px] md:text-[17px] font-mono font-bold text-white tracking-wider break-all">
                      {CONTACT.donationIban}
                    </code>
                    <button
                      type="button"
                      onClick={copyIban}
                      className={`shrink-0 h-9 px-3 rounded-lg font-bold text-[12px] uppercase tracking-wider transition-colors flex items-center gap-1.5 ${
                        copied ? "bg-emerald-500 text-white" : "bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55]"
                      }`}
                    >
                      {copied ? (<><Check className="h-3.5 w-3.5" /> Copiato</>) : (<><Copy className="h-3.5 w-3.5" /> Copia</>)}
                    </button>
                  </div>
                </div>

                <div className="mt-5 grid sm:grid-cols-2 gap-4 text-[13px]">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-300">Intestatario</div>
                    <div className="font-semibold text-white mt-0.5">{CONTACT.donationHolder}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-300">Causale</div>
                    <div className="font-semibold text-white mt-0.5">{current.causale}</div>
                  </div>
                </div>
              </div>

              <Button onClick={copyIban} className="mt-6 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-bold h-11 px-5">
                <Copy className="mr-2 h-4 w-4" />
                Copia IBAN per il bonifico
              </Button>
            </div>
          </div>

          {/* AIRC Donation Card */}
          <div className="lg:col-span-2 reveal rounded-3xl bg-white border border-slate-200 p-8 md:p-10 flex flex-col shadow-[0_30px_80px_-40px_rgba(16,42,107,0.25)]">
            <div className="flex items-center gap-3">
              <AIRCLogo />
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#e30613]">
                  Insieme alla ricerca
                </div>
                <h3 className="text-xl font-extrabold text-[#0a1f55] mt-0.5">Sostieni AIRC</h3>
              </div>
            </div>

            <p className="mt-5 text-slate-600 leading-relaxed text-[14.5px] flex-1">
              A.C.D.T. promuove la solidarietà e il sostegno alla ricerca scientifica.
              Con un piccolo gesto puoi contribuire alla ricerca contro il cancro tramite la{" "}
              <span className="font-semibold text-[#0a1f55]">Fondazione AIRC</span>.
            </p>

            <ul className="mt-5 space-y-2 text-[13px] text-slate-700">
              <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 text-[#e30613]" /><span>Donazione sicura sul sito ufficiale AIRC</span></li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 text-[#e30613]" /><span>Detrazione fiscale prevista per legge</span></li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 text-[#e30613]" /><span>Ogni contributo sostiene la ricerca</span></li>
            </ul>

            <a href={CONTACT.airc} target="_blank" rel="noreferrer" className="mt-6">
              <Button className="w-full h-12 bg-[#e30613] hover:bg-[#b8050f] text-white font-bold">
                <AIRCLogoSmall />
                <span className="ml-2">Dona ad AIRC</span>
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>

            <p className="mt-3 text-[11.5px] text-slate-500 text-center">
              Verrai reindirizzato al sito ufficiale di Fondazione AIRC
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function AIRCLogo() {
  return (
    <div className="relative h-16 w-16 rounded-2xl bg-white border-2 border-[#e30613] flex flex-col items-center justify-center shadow-md shadow-[#e30613]/15 overflow-hidden">
      <span className="font-extrabold text-[#e30613] text-[18px] tracking-tight leading-none">AIRC</span>
      <span className="mt-0.5 font-semibold text-[#e30613] text-[7px] tracking-[0.05em] leading-none uppercase">
        Fondazione
      </span>
    </div>
  );
}

function AIRCLogoSmall() {
  return (
    <span className="inline-flex items-center justify-center h-6 px-2 rounded bg-white text-[#e30613] font-extrabold text-[11px] tracking-wider">
      AIRC
    </span>
  );
}
